import React, {useState, useEffect} from 'react';
import styles from './TokenDistributionHistory.module.css';

const Distribution_USDI = 'https://distribution-calculator-256611876551.europe-west1.run.app';

const TokenDistributionHistory = ({refreshTrigger}) => {
    const [groupedDistributions, setGroupedDistributions] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [selectedBatchDetails, setSelectedBatchDetails] = useState([]);
    const [loading, setLoading] = useState(false);

    // 组件挂载时和 refreshTrigger 变化时获取数据
    useEffect(() => {
        fetchAllDistributions();
    }, [refreshTrigger]);

    // 获取所有分发记录并分组
    const fetchAllDistributions = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${Distribution_USDI}/get-token-distributes`);
            if (!response.ok) {
                throw new Error('Failed to fetch distributions');
            }
            const result = await response.json();

            if (result && result.data) {
                // 按 created_time 分组
                const grouped = groupByCreatedTime(result.data);
                setGroupedDistributions(grouped);
            }
        } catch (error) {
            console.error('Error fetching all distributions:', error);
        } finally {
            setLoading(false);
        }
    };

    // 按 created_time 分组数据
    const groupByCreatedTime = (data) => {
        const groups = {};

        data.forEach(item => {
            // 使用规范化的时间作为分组键
            const normalizedTime = item.created_time ? item.created_time.split('.')[0] : 'unknown';

            if (!groups[normalizedTime]) {
                groups[normalizedTime] = [];
            }
            groups[normalizedTime].push(item);
        });

        // 转换为数组并计算汇总信息
        const groupedArray = Object.entries(groups).map(([createdTime, items]) => {
            const totalAmount = items.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
            const allSuccess = items.every(item => item.signature && item.signature.trim() !== '');
            const tokenMintAddress = items[0]?.token_mint_address || '';

            return {
                created_time: createdTime,
                token_mint_address: tokenMintAddress,
                total_amount: totalAmount.toFixed(6),
                status: allSuccess ? 'Success' : 'Pending',
                count: items.length,
                items: items
            };
        });

        // 按时间倒序排序（最新的在前）
        return groupedArray.sort((a, b) => new Date(b.created_time) - new Date(a.created_time));
    };

    // 点击行查看详情
    const handleRowClick = (group) => {
        setSelectedBatchDetails(group.items);
        setShowModal(true);
    };

    // 关闭模态框
    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedBatchDetails([]);
    };

    // 格式化时间为可读的 UTC 时间
    const formatUTCTime = (timeStr) => {
        if (!timeStr) return '-';
        try {
            const date = new Date(timeStr);
            return date.toUTCString();
        } catch (e) {
            return timeStr;
        }
    };

    // 格式化时间为本地时间
    const formatLocalTime = (timeStr) => {
        if (!timeStr) return '-';
        try {
            const date = new Date(timeStr);
            return date.toLocaleString();
        } catch (e) {
            return timeStr;
        }
    };

    // 下载未分发的CSV
    const handleDownloadUndistributed = (e, group) => {
        e.stopPropagation(); // 防止触发行点击事件

        // 筛选出未分发的记录（没有signature的）
        const undistributedItems = group.items.filter(item => !item.signature || item.signature.trim() === '');

        if (undistributedItems.length === 0) {
            alert('No undistributed records found in this batch.');
            return;
        }

        // 创建CSV内容
        const csvContent = undistributedItems.map(item => `${item.address},${item.amount}`).join('\n');

        // 创建并下载文件
        const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'});
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        // 使用创建时间作为文件名的一部分
        const timestamp = group.created_time.replace(/[:\s]/g, '-');
        link.setAttribute('href', url);
        link.setAttribute('download', `undistributed_${timestamp}.csv`);
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (loading) {
        return <div className={styles.loading}>Loading distribution history...</div>;
    }

    return (
        <>
            <div className={styles.distributionHistory}>
                <h3>Distribution History</h3>
                {groupedDistributions.length === 0 ? (
                    <p className={styles.noData}>No distribution records found.</p>
                ) : (
                    <div className={styles.tableContainer}>
                        <table className={styles.historyTable}>
                            <thead>
                            <tr>
                                <th>#</th>
                                <th>Created Time</th>
                                <th>Token Mint Address</th>
                                <th>Sum of Distributed Amount</th>
                                <th>Status</th>
                                <th>Download undistributed CSV</th>
                            </tr>
                            </thead>
                            <tbody>
                            {groupedDistributions.map((group, index) => (
                                <tr
                                    key={group.created_time}
                                    onClick={() => handleRowClick(group)}
                                    className={styles.clickableRow}
                                >
                                    <td>{groupedDistributions.length - index}</td>
                                    <td>{formatLocalTime(group.created_time)}</td>
                                    <td className={styles.addressCell}>{group.token_mint_address}</td>
                                    <td>{group.total_amount}</td>
                                    <td className={group.status === 'Success' ? styles.statusSuccess : styles.statusPending}>
                                        {group.status}
                                    </td>
                                    <td>
                                        {group.status === 'Pending' && (
                                            <button
                                                className={styles.downloadButton}
                                                onClick={(e) => handleDownloadUndistributed(e, group)}
                                                title="Download undistributed addresses"
                                            >
                                                Download CSV
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* 详情模态框 */}
            {showModal && (
                <div className={styles.modalOverlay} onClick={handleCloseModal}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <button
                            className={styles.closeButton}
                            onClick={handleCloseModal}
                        >
                            ×
                        </button>
                        <h3>Distribution Details</h3>
                        <div className={styles.modalInfo}>
                            <p><strong>Created Time:</strong> {formatLocalTime(selectedBatchDetails[0]?.created_time)}
                            </p>
                            <p><strong>Token Mint Address:</strong> {selectedBatchDetails[0]?.token_mint_address}</p>
                            <p><strong>Total Records:</strong> {selectedBatchDetails.length}</p>
                        </div>
                        <div className={styles.tableWrapper}>
                            <table className={styles.detailTable}>
                                <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Address</th>
                                    <th>Amount</th>
                                    <th>Created Time</th>
                                    <th>Distributed Time</th>
                                    <th>Signature</th>
                                </tr>
                                </thead>
                                <tbody>
                                {selectedBatchDetails.map((item) => (
                                    <tr key={item.id}>
                                        <td>{item.id}</td>
                                        <td className={styles.addressCell}>{item.address}</td>
                                        <td>{item.amount}</td>
                                        <td>{formatLocalTime(item.created_time)}</td>
                                        <td>{formatLocalTime(item.distributed_time) || '-'}</td>
                                        <td className={styles.signatureCell}>
                                            {item.signature ? (
                                                <a
                                                    href={`https://solscan.io/tx/${item.signature}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    {item.signature.substring(0, 20)}...
                                                </a>
                                            ) : '-'}
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default TokenDistributionHistory;